// WishChatPanel — pair-scoped chat thread on a wish (W35 §3.2).
//
// Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §1.1:
// - message list (newest first, 50 per page)
// - per-row: sender avatar + body + relative time + delete (author-only)
// - input row at the bottom: textarea + 送出 button
// - empty state: "還沒有留言。留下第一個心願紀錄吧。"
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~7KB).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useToast } from "@/components/ui/toast";
import { toApiDate } from "@/lib/utils/date";
import {
  createChatMessage,
  deleteChatMessage,
  listChatMessages,
  type WishChatMessageOut,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";
import { useDraft } from "@/lib/hooks/useDraft";

export interface WishChatPanelProps {
  wishId: string;
  className?: string;
}

const PAGE_SIZE = 50;

function relativeTime(iso: string): string {
  // `toApiDate`, not `new Date(iso)`: the API sends naive UTC, and JS parses a
  // zone-less string as local. In HKT that made a message posted seconds ago
  // read 「8 小時前」 on a real render.
  const diffSec = Math.round((Date.now() - toApiDate(iso).getTime()) / 1000);
  if (diffSec < 60) return "剛剛";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} 分鐘前`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} 小時前`;
  return `${Math.round(diffSec / 86400)} 天前`;
}

export function WishChatPanel({ wishId, className }: WishChatPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  // W39.75 — measured, not assumed: the same probe that found the diary editor
  // losing a half-written entry (`docs/audit/probe-session-expiry.mjs`, pointed
  // at this page) reports `drafts parked: []` and an empty box after signing back
  // in. A message addressed to your partner is worth no less than a diary entry,
  // and rotate-on-action means the session dies on a second phone **as a matter
  // of course**.
  const chatDraft = useDraft(`wish-chat:${wishId}`);
  const [draft, setDraft] = useState(chatDraft.value);

  const query = useQuery({
    queryKey: ["wishes", "detail", wishId, "messages"],
    queryFn: () => listChatMessages(wishId, { limit: PAGE_SIZE }),
    enabled: Boolean(wishId),
    staleTime: 15_000,
  });

  const create = useMutation({
    mutationFn: (body: string) => createChatMessage(wishId, { body }),
    onSuccess: () => {
      setDraft("");
      // W39.75 — cleared only once the message is on the server, for the same
      // reason the diary draft is: clearing on unmount would defeat it, since the
      // unmount is the thing being survived.
      chatDraft.clear();
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "messages"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "events"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: () => {
      toast.push({ message: "送出留言失敗", variant: "danger" });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteChatMessage(wishId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "messages"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const messages: WishChatMessageOut[] = query.data?.items ?? [];

  return (
    <Card
      data-component="WishChatPanel"
      data-testid="wish-chat-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center gap-2 mb-s-3">
        <MessageCircle className="h-4 w-4" aria-hidden />
        <h3 className="font-display text-h3 text-ink-1">
          心願留言
          <span className="text-meta text-ink-3 font-normal ml-2">
            {query.data?.total ?? 0}
          </span>
        </h3>
      </header>
      {query.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : query.isError ? (
        // W54.0 B1 — a failed read is not an empty thread. The old chain had
        // no error branch, so a network failure fell into 「還沒有留言」 and
        // asked the person to leave the first message — for a thread that
        // already had one. The empty state belongs to "there is nothing here",
        // which is a different fact from "we could not find out".
        <ErrorState onRetry={() => void query.refetch()} />
      ) : messages.length === 0 ? (
        <p className="text-meta text-ink-3">
          還沒有留言。留下第一個心願紀錄吧。
        </p>
      ) : (
        <ol className="flex flex-col gap-s-3 mb-s-3">
          {messages.map((m) => {
            const isAuthor = user && m.user_id === user.id;
            const senderName = m.sender?.display_name ?? "未知使用者";
            return (
              <li
                key={m.id}
                data-component="WishChatMessage"
                data-testid={`wish-chat-message-${m.id}`}
                data-self={isAuthor ? "true" : "false"}
                className="flex items-start gap-2"
              >
                <span
                  className="shrink-0 w-7 h-7 rounded-full bg-paper-deep text-ink-2 flex items-center justify-center text-meta font-semibold"
                  aria-hidden
                >
                  {senderName.slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="flex items-baseline gap-2 text-meta text-ink-3">
                    <span className="font-semibold text-ink-1 text-body-sm">
                      {senderName}
                    </span>
                    <span className="tabular-nums">
                      {relativeTime(m.created_at)}
                    </span>
                    {m.edited_at ? (
                      <span className="text-meta">(已編輯)</span>
                    ) : null}
                  </p>
                  <p className="text-body-sm text-ink-1 whitespace-pre-wrap break-words mt-0.5">
                    {m.body}
                  </p>
                </div>
                {isAuthor ? (
                  <button
                    type="button"
                    onClick={() => del.mutate(m.id)}
                    disabled={del.isPending}
                    data-testid={`wish-chat-message-delete-${m.id}`}
                    aria-label="刪除留言"
                    className="press shrink-0 min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-3 hover:bg-paper-deep disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <form
        data-testid="wish-chat-form"
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const body = draft.trim();
          if (body.length === 0) return;
          create.mutate(body);
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            chatDraft.save(e.target.value);
          }}
          placeholder="留下你的心願紀錄…"
          rows={2}
          maxLength={1000}
          data-testid="wish-chat-input"
          className="flex-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1 placeholder:text-ink-3 resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink-1 focus-visible:outline-offset-2"
        />
        <Button
          type="submit"
          disabled={create.isPending || draft.trim().length === 0}
          data-testid="wish-chat-submit"
          className="press min-h-11 px-s-4 rounded-full bg-ink-1 text-paper"
        >
          <Send className="h-4 w-4" aria-hidden />
          送出
        </Button>
      </form>
    </Card>
  );
}